import {
  AutoAssignmentRuleType,
  QueryFieldType,
  TeamType,
  TenantInventorySettingsType,
  TenantBusinessType,
  TenantType,
  UserType,
} from "@/interfaces";
import { useAuthStore } from "@/states";
import { useGenericStore } from "@/states/generic";
import { canManageUsers } from "@/lib/permissions";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import axios from "axios";
import { App as AntApp } from "antd";

type AdminPaginationType = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export const ADMIN_USER_ROLE_OPTIONS = [
  { label: "Super Admin", value: "super_admin" },
  { label: "Owner", value: "owner" },
  { label: "Admin", value: "admin" },
  { label: "Campaigns", value: "sales" },
  { label: "Operations", value: "operations" },
  { label: "Accounts", value: "accounts" },
  { label: "Guest", value: "guest" },
] as const;

export type AdminTenantItemType = {
  allowedCountries?: string[] | null;
  tenantType?: TenantBusinessType;
  agentPortalEnabled?: boolean;
  id: string;
  name: string;
  country: string;
  currency: string[];
  maxUsers: number;
  showAISettings?: boolean;
  whatsappEnabled?: boolean;
  inventorySettings?: TenantInventorySettingsType;
  isActive: boolean;
  image?: string | null;
  subscriptionPlan?: "trial" | "paid";
  subscriptionStartDate?: string;
  trialEndDate?: string | null;
  subscriptionRenewalDate?: string | null;
  subscriptionComments?: string | null;
};

export type AdminLocationItemType = {
  id: string;
  country: string;
  state: string | null;
  city: string;
  currency: string | null;
  timeZone: string | null;
  airportCode: string | null;
  airportName: string | null;
  tenantId: string | null;
  tenant?: { id: string; name: string } | null;
  scope: "global" | "tenant";
  reviewStatus: "unreviewed" | "reviewed" | "archived";
  mergedIntoLocationId: string | null;
  matchingGlobalLocationId: string | null;
  createdAt: string;
};

export type AdminLocationCountryOptionType = {
  country: string;
  currency: string | null;
};

export type AdminCreateTenantPayloadType = {
  allowedCountries?: string[] | null;
  tenantType?: TenantBusinessType;
  agentPortalEnabled?: boolean;
  subscriptionPlan?: "trial" | "paid";
  subscriptionStartDate?: string;
  trialEndDate?: string | null;
  subscriptionRenewalDate?: string | null;
  subscriptionComments?: string | null;
  name: string;
  country: string;
  currency: string[];
  maxUsers: number;
  showAISettings?: boolean;
  whatsappEnabled?: boolean;
  inventorySettings: TenantInventorySettingsType;
  isActive: boolean;
  image?: string;
};

export type AdminUpdateTenantPayloadType = {
  allowedCountries?: string[] | null;
  tenantType?: TenantBusinessType;
  agentPortalEnabled?: boolean;
  subscriptionPlan?: "trial" | "paid";
  subscriptionStartDate?: string;
  trialEndDate?: string | null;
  subscriptionRenewalDate?: string | null;
  subscriptionComments?: string | null;
  id: string;
  name: string;
  country: string;
  currency: string[];
  maxUsers: number;
  showAISettings?: boolean;
  whatsappEnabled?: boolean;
  inventorySettings: TenantInventorySettingsType;
  isActive: boolean;
  image?: string | null;
};

export type AdminCreateLocationPayloadType = {
  country: string;
  state?: string | null;
  city: string;
  currency?: string | null;
  timeZone?: string | null;
  airportCode?: string | null;
  airportName?: string | null;
};

export type AdminUpdateLocationPayloadType = {
  id: string;
  country?: string;
  state?: string | null;
  city?: string;
  currency?: string | null;
  timeZone?: string | null;
  airportCode?: string | null;
  airportName?: string | null;
  updateAllCountryCurrency?: boolean;
};

export type AdminUserItemType = {
  id: string;
  fullname: string;
  email: string;
  role: string;
  tenantId: string | null;
  isActive: boolean;
  tenant?: {
    id: string;
    name: string;
  };
};

type AdminListResponseType<T> = {
  data: T[];
  pagination: AdminPaginationType;
};

type AdminImpersonationResponseType = {
  token: string;
  expiresAt: number;
  user: {
    id: string;
    role: string;
    tenantId: string;
    fullname: string;
    email: string;
  };
  impersonation: {
    userId: string;
    fullname: string;
    email: string;
    role?: string;
    tenantId: string;
    tenantName?: string;
  };
};

type LoginResponse = { token: string; user: UserType; requiresMfa?: false } | {
  requiresMfa: true;
  requiresMfaSetup: boolean;
  partialToken: string;
};
type SessionConfirmation = { requiresSessionConfirmation: true; message: string };

/** Provides CRM login and MFA requests with explicit approval to replace other sessions. */
export const useUserActions = () => {
  const { modal } = AntApp.useApp();
  const setUser = useAuthStore((state) => state.setUser);
  const setToken = useAuthStore((state) => state.setToken);
  const clearAuthSnapshot = useAuthStore((state) => state.clearAuthSnapshot);

  const authenticate = async (url: string, payload: Record<string, string>): Promise<LoginResponse | null> => {
    const options = { withCredentials: true, _skipAuthRefresh: true } as Parameters<typeof axios.post>[2];
    let res = await axios.post<LoginResponse | SessionConfirmation>(url, payload, options);
    if ("requiresSessionConfirmation" in res.data) {
      const confirmed = await modal.confirm({
        title: "Sign out from other places?",
        content: res.data.message,
        okText: "Sign out others and continue",
        cancelText: "Cancel login",
        autoFocusButton: "cancel",
        maskClosable: false,
      });
      if (!confirmed) return null;
      res = await axios.post<LoginResponse | SessionConfirmation>(url, { ...payload, confirmSignOutOthers: true }, options);
    }
    if ("requiresSessionConfirmation" in res.data) throw new Error("Please try signing in again");
    if ("token" in res.data && res.data.token) {
      clearAuthSnapshot();
      setUser(res.data.user);
      setToken(res.data.token);
      return res.data;
    }
    if (res.data.requiresMfa) return res.data;
    throw new Error("Failed to login");
  };

  const login = (payload: { username: string; password: string }) => authenticate("/api/auth/login", payload);
  const verifyMfa = (payload: { partialToken: string; mfaCode: string }) => authenticate("/api/auth/verify-mfa", payload);
  return { login, verifyMfa };
};

/**
 * Hook providing logout functionality. 
 * Returned as 'refetch' to maintain compatibility with the Interceptor.
 */
export const useLogout = () => {
  const resetAuth = useAuthStore((state) => state.reset);
  const resetGeneric = useGenericStore((state) => state.reset);
  const client = useQueryClient();

  const logout = async () => {
    try {
      // Notify server to revoke refresh token and clear HttpOnly cookie
      await axios.post(`/api/auth/logout`, {}, { withCredentials: true, _skipAuthRefresh: true } as any);
    } catch (error) {
      console.error("Logout API notification failed", error);
    } finally {
      // Always clear local state regardless of API success
      resetAuth();
      resetGeneric();
      client.clear();

      // Force a full page reload to the login screen to clear all memory/states
      window.location.href = "/login";
    }
  };

  return { refetch: logout };
};

// --- Standard User Queries ---

/**
 * Refreshes the current user's tenant or tenant-independent platform profile.
 */
export const useMyUser = (token: string | null) => {
  const setUser = useAuthStore((state) => state.setUser);
  return useQuery({
    queryKey: ["me", token],
    queryFn: async ({ signal }): Promise<UserType> => {
      const res = await axios.get(`/api/users/me`, { signal });
      setUser(res.data); // Sync store with latest DB data
      return res.data;
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Fetches a specific user by ID
 */
export const useGetUser = (id?: string) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: async (): Promise<UserType> => {
      const res = await axios.get(`/api/users/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
};

/**
 * Lists all users for the current tenant
 */
export const useListUsersQuery = (tenantId?: string) => {
  return useQuery({
    queryKey: tenantId ? ["list-users", tenantId] : ["list-users"],
    queryFn: async (): Promise<UserType[]> => {
      const res = await axios.get(`/api/users`);
      return res.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Fetches tenant organisation details, optionally refreshing admin-controlled availability.
 */
export const useGetTenantQuery = (tenantId?: string, refreshAvailability = false) => {
  return useQuery({
    queryKey: tenantId ? ["tenant", tenantId] : ["tenant"],
    queryFn: async (): Promise<TenantType> => {
      const res = await axios.get(`/api/tenants`);
      return res.data;
    },
    staleTime: refreshAvailability ? 0 : 10 * 60 * 1000,
    refetchInterval: refreshAvailability ? 30_000 : false,
    refetchOnWindowFocus: refreshAvailability ? "always" : true,
  });
};

/**
 * Fetches mailbox access list for a given user.
 * @param userId User identifier
 */
export const useGetUserMailboxAccess = (userId?: string) => {
  return useQuery({
    queryKey: ["user-mailbox-access", userId],
    queryFn: async (): Promise<{ data: string[]; hasFullAccess: boolean }> => {
      const res = await axios.get(`/api/users/${userId}/mailbox-access`);
      return res.data;
    },
    enabled: !!userId,
  });
};

/**
 * Updates mailbox access list for a given user.
 */
export const useUpdateUserMailboxAccessMutation = () => {
  return useMutation({
    mutationFn: async ({
      userId,
      mailboxIds,
    }: {
      userId: string;
      mailboxIds: string[];
    }): Promise<{ message: string }> => {
      const res = await axios.put(`/api/users/${userId}/mailbox-access`, { mailboxIds });
      return res.data;
    },
  });
};

export type UserWhatsAppAccountType = { id: string; displayName: string; createdAt: string };
export type UserWhatsAppAccessType = { data: string[]; hasFullAccess: boolean };

const useUserWhatsAppScope = (tenantId?: string) => {
  const user = useAuthStore((state) => state.user);
  const effectiveTenantId = user?.role === "super_admin" && tenantId
    ? tenantId
    : user?.tenantId ?? user?.tenant?.id;
  return { tenantId: effectiveTenantId, userId: user?.id, enabled: canManageUsers(user?.role) };
};

/** Lists account metadata for access assignment, including while WhatsApp is disabled. */
export const useListUserWhatsAppAccountsQuery = (tenantId?: string, enabled = true) => {
  const scope = useUserWhatsAppScope(tenantId);
  return useQuery({
    queryKey: ["user-whatsapp-accounts", scope.tenantId, scope.userId],
    queryFn: async ({ signal }): Promise<{ data: UserWhatsAppAccountType[] }> =>
      (await axios.get<{ data: UserWhatsAppAccountType[] }>("/api/users/whatsapp-accounts", { signal })).data,
    enabled: scope.enabled && enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

/** Fetches the explicit account assignments and inherited access for a tenant user. */
export const useGetUserWhatsAppAccess = (userId?: string, tenantId?: string, enabled = true) => {
  const scope = useUserWhatsAppScope(tenantId);
  return useQuery({
    queryKey: ["user-whatsapp-access", scope.tenantId, scope.userId, userId],
    queryFn: async ({ signal }): Promise<UserWhatsAppAccessType> =>
      (await axios.get<UserWhatsAppAccessType>(`/api/users/${userId}/whatsapp-access`, { signal })).data,
    enabled: scope.enabled && enabled && Boolean(userId),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

/** Saves account assignments and refreshes the affected tenant's account and user caches. */
export const useUpdateUserWhatsAppAccessMutation = (tenantId?: string) => {
  const scope = useUserWhatsAppScope(tenantId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, accountIds }: { userId: string; accountIds: string[] }): Promise<UserWhatsAppAccessType> =>
      (await axios.put<UserWhatsAppAccessType>(`/api/users/${userId}/whatsapp-access`, { accountIds })).data,
    onSuccess: async (_result, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["user-whatsapp-access", scope.tenantId, scope.userId, variables.userId] }),
        queryClient.invalidateQueries({ queryKey: ["user-whatsapp-accounts", scope.tenantId, scope.userId] }),
        queryClient.invalidateQueries({ queryKey: ["whatsapp", scope.tenantId] }),
        queryClient.invalidateQueries({ queryKey: ["user", variables.userId] }),
        queryClient.invalidateQueries({ queryKey: ["list-users"] }),
        queryClient.invalidateQueries({ queryKey: ["me"] }),
      ]);
    },
  });
};

// --- Team & Organisation Queries ---

/**
 * Lists all teams for the current tenant
 */
export const useListTeamsQuery = (tenantId?: string) => {
  return useQuery({
    queryKey: tenantId ? ["list-teams", tenantId] : ["list-teams"],
    queryFn: async (): Promise<TeamType[]> => {
      const res = await axios.get(`/api/teams`);
      return res.data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Fetches team details including member names
 */
export const useGetTeamQuery = (id?: string, tenantId?: string) => {
  return useQuery({
    queryKey: tenantId ? ["team", tenantId, id] : ["team", id],
    queryFn: async (): Promise<TeamType> => {
      const res = await axios.get(`/api/teams/${id}`);
      // Fetch names for member IDs to provide a better UI
      res.data.members = await Promise.all(
        res.data.members.map(async (memberId: string) => {
          try {
            const userRes = await axios.get(`/api/users/${memberId}`);
            return {
              id: memberId,
              name: userRes.data.fullname || userRes.data.username,
            };
          } catch {
            return { id: memberId, name: "Unknown User" };
          }
        }),
      );
      return res.data;
    },
    enabled: !!id,
  });
};

/**
 * Fetches tenant-specific query fields for lead filtering
 */
export const useGetTenantQueryFields = (tenantId?: string) => {
  return useSuspenseQuery({
    queryKey: tenantId ? ["tenant-query-fields", tenantId] : ["tenant-query-fields"],
    queryFn: async (): Promise<QueryFieldType[]> => {
      const res = await axios.get(`/api/tenants/query-fields`);
      return res.data.queryFields || [];
    },
    staleTime: 1000 * 60 * 60, // 1 hour (rarely changes)
  });
};

/**
 * Fetches auto-assignment rules for lead distribution
 */
export const useGetAutoAssignmentRules = (tenantId?: string) => {
  return useQuery({
    queryKey: tenantId ? ["tenant-auto-assignment-rules", tenantId] : ["tenant-auto-assignment-rules"],
    queryFn: async (): Promise<AutoAssignmentRuleType[]> => {
      const res = await axios.get(`/api/tenants/auto-assignment-rules`);
      return res.data.rules || [];
    },
  });
};

/**
 * Mutation to update lead auto-assignment logic
 */
export const useUpdateAutoAssignmentRules = () => {
  return useMutation({
    mutationFn: async (
      rules: AutoAssignmentRuleType[],
    ): Promise<{ message: string; rules: AutoAssignmentRuleType[] }> => {
      const res = await axios.put(`/api/tenants/auto-assignment-rules`, { rules });
      return res.data;
    },
  });
};

/**
 * Lists tenants for super-admin management with search/pagination.
 */
export const useAdminListTenantsQuery = ({
  page = 1,
  limit = 20,
  search = "",
  country = "",
  currency = "",
  isActive,
  sortBy = "createdAt",
  sortOrder = "DESC",
}: {
  page?: number;
  limit?: number;
  search?: string;
  country?: string;
  currency?: string;
  isActive?: boolean;
  sortBy?: "name" | "country" | "isActive" | "createdAt" | "updatedAt";
  sortOrder?: "ASC" | "DESC";
}) => {
  return useQuery({
    queryKey: ["admin-tenants", page, limit, search, country, currency, isActive, sortBy, sortOrder],
    queryFn: async (): Promise<AdminListResponseType<AdminTenantItemType>> => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (search.trim()) params.set("search", search.trim());
      if (country.trim()) params.set("country", country.trim());
      if (currency.trim()) params.set("currency", currency.trim());
      if (typeof isActive === "boolean") params.set("isActive", String(isActive));
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);
      const res = await axios.get(`/api/admin/tenants?${params.toString()}`);
      return res.data;
    },
    staleTime: 60 * 1000,
  });
};

/** Loads one tenant's complete editable platform settings for the super-admin page. */
export const useAdminTenantQuery = (tenantId?: string) => useQuery({
  queryKey: ["admin-tenant", tenantId],
  enabled: Boolean(tenantId),
  queryFn: async (): Promise<AdminTenantItemType> => (await axios.get(`/api/admin/tenants/${tenantId}`)).data,
  refetchOnWindowFocus: false,
});

/**
 * Creates a tenant from super-admin panel and refreshes tenant list cache.
 */
export const useAdminCreateTenantMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AdminCreateTenantPayloadType): Promise<AdminTenantItemType> => {
      const res = await axios.post(`/api/admin/tenants`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
  });
};

/**
 * Updates a tenant from super-admin panel and refreshes tenant caches.
 */
export const useAdminUpdateTenantMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }: AdminUpdateTenantPayloadType): Promise<AdminTenantItemType> => {
      const res = await axios.put(`/api/admin/tenants/${id}`, payload);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(["admin-tenant", variables.id], _data);
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenant"] });
      queryClient.invalidateQueries({ queryKey: ["tenant", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp", variables.id] });
      if (Object.prototype.hasOwnProperty.call(variables, "allowedCountries")) {
        for (const key of ["country", "country-select", "destination", "cities", "city-locations", "location-values", "tenant-locations", "flight-locations", "timezone-values", "agent-portal-destination-options"]) {
          queryClient.invalidateQueries({ queryKey: [key] });
        }
      }
    },
  });
};

/**
 * Lists locations for super-admin management with pagination/filtering/sorting.
 */
export const useAdminListLocationsQuery = ({
  page = 1,
  limit = 20,
  search = "",
  country = "",
  currency = "",
  sortBy = "country",
  sortOrder = "ASC",
  scope = "all",
  reviewStatus = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
  country?: string;
  currency?: string;
  sortBy?: "country" | "state" | "city" | "currency";
  sortOrder?: "ASC" | "DESC";
  scope?: "all" | "global" | "tenant";
  reviewStatus?: "" | "unreviewed" | "reviewed" | "archived";
}) => {
  return useQuery({
    queryKey: ["admin-locations", page, limit, search, country, currency, sortBy, sortOrder, scope, reviewStatus],
    queryFn: async (): Promise<AdminListResponseType<AdminLocationItemType>> => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (search.trim()) params.set("search", search.trim());
      if (country.trim()) params.set("country", country.trim());
      if (currency.trim()) params.set("currency", currency.trim());
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);
      if (scope !== "all") params.set("scope", scope);
      if (reviewStatus) params.set("reviewStatus", reviewStatus);
      const res = await axios.get(`/api/admin/locations?${params.toString()}`);
      return res.data;
    },
    staleTime: 60 * 1000,
  });
};

/**
 * Lists distinct countries from the locations table for autocomplete in admin forms.
 */
export const useAdminLocationCountriesQuery = (search = "", globalOnly = false) => {
  return useQuery({
    queryKey: ["admin-location-countries", search, globalOnly],
    queryFn: async (): Promise<AdminLocationCountryOptionType[]> => {
      const params = new URLSearchParams();
      if (globalOnly) params.set("globalOnly", "true");
      if (search.trim()) {
        params.set("search", search.trim());
      }
      const queryString = params.toString();
      const res = await axios.get(`/api/admin/locations/countries${queryString ? `?${queryString}` : ""}`);
      return Array.isArray(res.data) ? res.data : [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Creates a location from super-admin panel and refreshes location list cache.
 */
export const useAdminCreateLocationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AdminCreateLocationPayloadType): Promise<AdminLocationItemType> => {
      const res = await axios.post(`/api/admin/locations`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-locations"] });
      queryClient.invalidateQueries({ queryKey: ["flight-locations"] });
      queryClient.invalidateQueries({ queryKey: ["timezone-values"] });
    },
  });
};

/**
 * Updates a location from super-admin panel and refreshes location list cache.
 */
export const useAdminUpdateLocationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }: AdminUpdateLocationPayloadType): Promise<AdminLocationItemType> => {
      const res = await axios.put(`/api/admin/locations/${id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-locations"] });
      queryClient.invalidateQueries({ queryKey: ["flight-locations"] });
      queryClient.invalidateQueries({ queryKey: ["timezone-values"] });
    },
  });
};

/**
 * Deletes a location from super-admin panel and refreshes location list cache.
 */
export const useAdminDeleteLocationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ success: boolean }> => {
      const res = await axios.delete(`/api/admin/locations/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-locations"] });
      queryClient.invalidateQueries({ queryKey: ["flight-locations"] });
      queryClient.invalidateQueries({ queryKey: ["timezone-values"] });
    },
  });
};

/** Reviews, archives or restores a location from the admin catalogue. */
export const useAdminReviewLocationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reviewStatus }: { id: string; reviewStatus: "unreviewed" | "reviewed" | "archived" }) => {
      const res = await axios.patch(`/api/admin/locations/${id}/review`, { reviewStatus });
      return res.data as AdminLocationItemType;
    },
    onSuccess: () => {
      for (const key of ["admin-locations", "tenant-locations", "city-locations", "cities", "country", "flight-locations", "timezone-values"]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
};

/** Promotes a tenant location or merges it into an existing global row. */
export const useAdminConsolidateLocationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, targetLocationId }: { id: string; targetLocationId?: string }) => {
      const res = await axios.post(`/api/admin/locations/${id}/consolidate`, { targetLocationId });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-locations"] });
      queryClient.invalidateQueries({ queryKey: ["city-locations"] });
    },
  });
};

/**
 * Lists users across tenants for super-admin search and impersonation.
 */
export const useAdminListUsersQuery = ({
  page = 1,
  limit = 20,
  search = "",
  tenantId,
  role,
  isActive,
  sortBy,
  sortOrder,
}: {
  page?: number;
  limit?: number;
  search?: string;
  tenantId?: string;
  role?: string;
  isActive?: boolean;
  sortBy?: "fullname" | "email" | "role" | "isActive" | "createdAt" | "updatedAt" | "tenantName";
  sortOrder?: "ASC" | "DESC";
}) => {
  return useQuery({
    queryKey: ["admin-users", page, limit, search, tenantId, role, isActive, sortBy, sortOrder],
    queryFn: async (): Promise<AdminListResponseType<AdminUserItemType>> => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (search.trim()) params.set("search", search.trim());
      if (tenantId) params.set("tenantId", tenantId);
      if (role) params.set("role", role);
      if (typeof isActive === "boolean") params.set("isActive", String(isActive));
      if (sortBy) params.set("sortBy", sortBy);
      if (sortOrder) params.set("sortOrder", sortOrder);
      const res = await axios.get(`/api/admin/users?${params.toString()}`);
      return res.data;
    },
    staleTime: 30 * 1000,
  });
};

/**
 * Starts impersonation and returns impersonated access token and metadata.
 */
export const useAdminImpersonateMutation = () => {
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }): Promise<AdminImpersonationResponseType> => {
      const res = await axios.post(`/api/admin/impersonate`, { userId });
      return res.data;
    },
  });
};
